import Chevron from 'src/components/dashboard/components/navigationMenu/components/chevron';
import FooterPrivacy from '../../../auth/components/baseTemplate/FooterPrivacy';
import { formatDateAndTime } from 'src/utils/formatDate';
import { formatPhoneNumber } from 'src/utils/formatPhoneNumber';
import { FundingService } from 'src/services/funding.service';
import { getBrandByDealerNumber } from 'src/utils/helpers';
import { IUser } from '../../../../utils/interfaces/user';
import Search from 'src/components/forms/funding_search';
import Spinner from 'src/components/ui/spinner';
import Table from 'src/components/table';
import TableFooter from 'src/components/table/pagination/tableFooter';
import { useLocalization } from 'src/utils/useLocalization';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    FundingSearchStatusOptions,
    FundingStatusTableData,
    IFundingMissingItems,
    IFundingStatusObject,
} from 'src/services/models/funding.service';
import { StatusDot, statusToStatusClass } from 'src/components/ui/statusDot';
import { useEffect, useState } from 'react';
import './styles.scss';

const NO_RESULTS = 0;
const DEFAULT_FUNDING_DETAILS = -1;

const loader = (
    <div className='loader_container'>
        <Spinner />
    </div>
);

interface IFundingDetailProps {
    dealerId: string;
    accountNumber: number;
    close: VoidFunction;
}

interface IFundingDetailHistoryProps {
    dealerId: string;
    accountNumber: number;
}

interface IFundingDetailHistoryEntryProps {
    entryData: IFundingStatusObject;
}

interface IFundingMissingItemsProps {
    data: IFundingMissingItems | string;
}

const FundingMissingItems: React.FC<IFundingMissingItemsProps> = ({ data }) => {
    const { fundingStatusDetails } = useLocalization();
    const { i18n } = useTranslation();
    const isFrench = i18n?.language?.includes('fr');
    if (typeof data === 'string') {
        return null;
    }

    function getArray(obj: any) {
        if (!obj) return [];
        let items = [];
        if (Array.isArray(obj)) {
            items = [...obj];
        } else {
            items = [obj];
        }
        return items;
    }

    function getTranslatedValue(value: string) {
        if (!value) return '';
        const translations = value.split('___');
        const NO_TRANSLATION = 1;
        const EN = 0;
        const FR = 1;

        return translations.length == NO_TRANSLATION || !isFrench
            ? translations[EN]
            : translations[FR];
    }

    const items = getArray(data.value);

    return (
        <>
            <span
                className='label-span'
                style={{ marginLeft: '20px', gridColumn: 'span 2' }}
            >
                {fundingStatusDetails.HISTORY_MISSING}
            </span>

            {items.map((item) => {
                const ZERO = 0;
                const values = getArray(item.Items.value);
                const comments = getArray(item.FunderComments?.value);
                const group = [...item.Group].reduce(
                    (a, c) => a + c.charCodeAt(ZERO),
                    ZERO,
                );
                return (
                    <>
                        <span
                            className='label-span'
                            style={{ marginLeft: '30px' }}
                        >
                            {getTranslatedValue(item.Group)}
                        </span>
                        <div className='item-span'>
                            {values.map((value, i) => (
                                <span key={`${group + i}-mc`}>
                                    {getTranslatedValue(value)}
                                </span>
                            ))}
                            {item.FunderComments
                                ? comments.map((comment, i) => (
                                      <span key={`${group + i}-fc`}>
                                          {getTranslatedValue(comment)}
                                      </span>
                                  ))
                                : null}
                        </div>
                    </>
                );
            })}
        </>
    );
};

const FundingPackageDetailHistoryEntry: React.FC<
    IFundingDetailHistoryEntryProps
> = ({ entryData }) => {
    const [expanded, setExpanded] = useState<boolean>(false);

    const chevronColor = '#363b49';
    const { statusFilterResponseToOptionMap, fundingStatusDetails } =
        useLocalization();
    const status = entryData.Status as FundingSearchStatusOptions;
    return (
        <div
            className={`funding-status-history-entry ${
                expanded ? 'expanded' : ''
            }`}
            onClick={() => setExpanded(!expanded)}
        >
            <div className='status'>
                <StatusDot color={statusToStatusClass(status)} />
                <span className='dot-label'>
                    {statusFilterResponseToOptionMap[status]}
                </span>
            </div>
            <div className='funding-status-col-2'>
                <span>{formatDateAndTime(entryData.StatusDate)}</span>
                <div className='funding-status-history-entry-chevron'>
                    <Chevron
                        className={`accordion__icon ${
                            expanded ? 'rotate-up' : 'rotate-down'
                        }`}
                        width={10}
                        fill={chevronColor}
                    />
                </div>
            </div>
            {!expanded ? (
                <></>
            ) : (
                <>
                    <span className='label-span' style={{ marginLeft: '20px' }}>
                        {fundingStatusDetails.HISTORY_AUDITOR}
                    </span>
                    <span>{entryData.AuditorName}</span>
                    <span className='label-span' style={{ marginLeft: '20px' }}>
                        {fundingStatusDetails.HISTORY_NOTES}
                    </span>
                    <span>{entryData.AuditorComments}</span>
                    <FundingMissingItems data={entryData.MissingItems} />
                </>
            )}
        </div>
    );
};

const FundingPackageDetailHistory: React.FC<IFundingDetailHistoryProps> = ({
    dealerId,
    accountNumber,
}) => {
    const [loading, setLoading] = useState<boolean>(true);
    const [historyEntries, setHistoryEntries] = useState<
        IFundingStatusObject[]
    >([]);

    useEffect(() => {
        setLoading(true);
        const service = new FundingService();
        service
            .fundingHistory(dealerId, accountNumber.toString())
            .then((response) => {
                if (!!response && !!response.data) {
                    setHistoryEntries(response.data as IFundingStatusObject[]);
                    setLoading(false);
                }
            });
    }, []);
    return loading ? (
        loader
    ) : (
        <>
            {historyEntries.map((entry) => (
                <FundingPackageDetailHistoryEntry
                    key={entry.Key}
                    entryData={entry}
                />
            ))}
        </>
    );
};

const FundingPackageDetail: React.FC<IFundingDetailProps> = ({
    dealerId,
    accountNumber,
    close,
}) => {
    const [loading, setLoading] = useState<boolean>(true);
    const [state, setState] = useState<IFundingStatusObject>();

    useEffect(() => {
        setLoading(true);
        const service = new FundingService();
        service
            .fundingDetails(dealerId, accountNumber.toString())
            .then((response) => {
                if (!!response && !!response.data) {
                    setState(response.data);
                    setLoading(false);
                }
            });
    }, []);

    const { statusFilterResponseToOptionMap, fundingStatusDetails } =
        useLocalization();

    const status = state?.Status as FundingSearchStatusOptions;

    return (
        <>
            <div className='funding-details-background'></div>
            <div className='funding-detail-container '>
                <div className='close-icon' onClick={() => close()}>
                    <i
                        className='c-icon  c-icon--[semantic-close] c-icon--large'
                        aria-hidden='true'
                        role={'img'}
                        style={{
                            height: '16px !important',
                            width: '16px !important',
                        }}
                    ></i>
                </div>
                <div className='funding-detail-wrapper'>
                    <div
                        className='page-title left-align'
                        style={{ marginBottom: '20px' }}
                    >
                        <h5>{fundingStatusDetails.HEADER_LABEL}</h5>
                    </div>
                    {loading ? (
                        loader
                    ) : (
                        <>
                            <div className='funding-details'>
                                <div className='funding-details-section-header'>
                                    <h6 style={{ marginTop: '0px' }}>
                                        {fundingStatusDetails.APPLICANT_HEADER}
                                    </h6>
                                </div>
                                <span className='label-span'>
                                    {fundingStatusDetails.APPLICANT_NAME}
                                </span>
                                <span>
                                    {state?.BusinessName.length
                                        ? state.BusinessName
                                        : `${state?.PersonName.FirstName} ${state?.PersonName.LastName}`}
                                </span>
                                <span className='label-span'>
                                    {fundingStatusDetails.APPLICANT_ADDRESS}
                                </span>
                                <span>
                                    {state?.ApplicantAddress.AddressLine1},{' '}
                                    {state?.ApplicantAddress.AddressLine2 !=
                                        null ||
                                    state?.ApplicantAddress.AddressLine2 != ''
                                        ? state?.ApplicantAddress.AddressLine2
                                        : ''}
                                    , {state?.ApplicantAddress.City},{' '}
                                    {state?.ApplicantAddress.State}{' '}
                                    {state?.ApplicantAddress.ZipCode.Code}
                                </span>
                                <span className='label-span'>
                                    {fundingStatusDetails.APPLICANT_HOME}
                                </span>
                                <span>
                                    {state != undefined
                                        ? formatPhoneNumber(
                                              state?.ApplicantHomePhoneNumber
                                                  .PhoneNumber,
                                          )
                                        : ''}
                                </span>
                                <span className='label-span'>
                                    {fundingStatusDetails.APPLICANT_MOBILE}
                                </span>
                                <span>
                                    {state != undefined
                                        ? formatPhoneNumber(
                                              state.ApplicantWorkPhoneNumber
                                                  .PhoneNumber,
                                          )
                                        : ''}
                                </span>
                                <span className='label-span'>
                                    {fundingStatusDetails.APPLICANT_SSN}
                                </span>
                                <span>{state?.SSN.replaceAll('*', 'x')}</span>
                                <div className='funding-details-section-header'>
                                    <h6>
                                        {`${state?.Vehicle.Year} ${state?.Vehicle.Make} ${state?.Vehicle.Model}`}
                                    </h6>
                                </div>
                                <span className='label-span'>
                                    {fundingStatusDetails.LOAN_AMOUNT}
                                </span>
                                <span>${state?.AmountFinanced}</span>
                                <span className='label-span'>
                                    {fundingStatusDetails.LOAN_TYPE}
                                </span>
                                <span>{state?.FinanceType}</span>
                                <span className='label-span'>
                                    {fundingStatusDetails.LOAN_TERM}
                                </span>
                                <span>
                                    {state?.Term}{' '}
                                    {fundingStatusDetails.LOAN_MONTHS}
                                </span>
                                <span className='label-span'>
                                    {fundingStatusDetails.LOAN_BULLETIN}
                                </span>
                                <span>{state?.BulletinNumber}</span>
                                <span className='label-span'>
                                    {fundingStatusDetails.LOAN_TOTAL}
                                </span>
                                <span>${state?.DisbursementAmount}</span>
                                <div className='funding-details-section-header'>
                                    <h6>
                                        {fundingStatusDetails.PACKAGE_HEADER}
                                    </h6>
                                </div>
                                <span className='label-span'>
                                    {fundingStatusDetails.PACKAGE_APPLICATION}
                                </span>
                                <span>{state?.ApplicationNumber}</span>
                                <span className='label-span'>
                                    {fundingStatusDetails.PACKAGE_CONTRACTID}
                                </span>
                                <span>{state?.ContractId}</span>
                                <span className='label-span'>
                                    {fundingStatusDetails.PACKAGE_STATUS}
                                </span>
                                <div className='status'>
                                    <StatusDot
                                        color={statusToStatusClass(status)}
                                    />
                                    <span className='dot-label'>
                                        {
                                            statusFilterResponseToOptionMap[
                                                status
                                            ]
                                        }
                                    </span>
                                </div>
                                <div className='funding-details-section-header'>
                                    <h6 className='status-history-header'>
                                        {fundingStatusDetails.HISTORY_HEADER}
                                    </h6>
                                </div>
                                <FundingPackageDetailHistory
                                    dealerId={dealerId}
                                    accountNumber={accountNumber}
                                />
                            </div>
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

const Application = () => {
    const currentDealers = useSelector((state: IUser) => {
        return state.currentDealers;
    });
    const dealerDataInfo = useSelector((state: IUser) => {
        return state.dealerDataInfo;
    });
    const [brand, setBrand] = useState<string>('vw6');
    const [dealerID, setDealerID] = useState('');
    const [content, setContent] = useState<FundingStatusTableData[]>([]);
    const [failedSearchTerm, setFailedSearchTerm] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(true);
    const [displayedContent, setDisplayedContent] = useState<
        FundingStatusTableData[]
    >([]);
    const [fundingDetail, setFundingDetail] = useState<number>(
        DEFAULT_FUNDING_DETAILS,
    );

    const { labelText, fundingStatus } = useLocalization();

    useEffect(() => {
        if (currentDealers && dealerDataInfo) {
            const { dealer } = dealerDataInfo;
            setBrand(getBrandByDealerNumber(dealer.brand, dealer.dealerNumber));
            setDealerID(dealer.dealerNumber);
        }
    }, [dealerDataInfo]);

    useEffect(() => {
        if (fundingDetail !== DEFAULT_FUNDING_DETAILS) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
    }, [fundingDetail]);

    return !!dealerID ? (
        <>
            <div className='funding-status-wrapper'>
                <div className='page-title'>
                    <h5>{fundingStatus.HEADER_LABEL}</h5>
                </div>
                <div className='search-container'>
                    <Search
                        updateTableContent={setContent}
                        dealerID={dealerID}
                        setLoading={setLoading}
                        setFailedSearchTerm={setFailedSearchTerm}
                    />
                </div>
                <div
                    className={`content-container ${
                        content.length === NO_RESULTS || loading
                            ? 'no-content'
                            : ''
                    }`}
                >
                    {!loading ? (
                        content.length > NO_RESULTS ? (
                            <>
                                <Table
                                    items={displayedContent}
                                    setFundingDetail={setFundingDetail}
                                />
                                <TableFooter
                                    brand={brand}
                                    content={content}
                                    setDisplayedContent={setDisplayedContent}
                                />
                            </>
                        ) : (
                            <div className='no-content'>
                                <div>
                                    {`${labelText.LABEL_TEXT_FUNDING_SEARCH_ERROR_NO_RESULTS} `}
                                    <span className='bold'>
                                        {failedSearchTerm}
                                    </span>
                                </div>
                                <div className='small-text'>
                                    {
                                        labelText.LABEL_TEXT_FUNDING_SEARCH_ERROR_SUGGESTION
                                    }
                                </div>
                            </div>
                        )
                    ) : (
                        loader
                    )}
                </div>
                <div className='privacy-footer-container '>
                    <FooterPrivacy />
                </div>
            </div>
            <>
                {fundingDetail !== DEFAULT_FUNDING_DETAILS && (
                    <FundingPackageDetail
                        dealerId={dealerID}
                        accountNumber={fundingDetail}
                        close={() => setFundingDetail(DEFAULT_FUNDING_DETAILS)}
                    />
                )}
            </>
        </>
    ) : null;
};
export default Application;
